// Phase 102: First-time user onboarding.
//
// A 3-step bottom-sheet carousel shown once per device when a new
// visitor lands on the app. Three guards prevent it from nagging
// existing users:
//   1. user_prefs.onboarding.completed_at set   → never show
//   2. user_prefs.onboarding.dismissed_at set   → never show
//   3. tx_count >= 10 (auto-skip heuristic)     → never show
//
// Power-user escape hatch: `?skip=onboard` in the URL. Useful for
// re-installs / dev testing.
//
// Re-trigger: Settings → Tutorial (sets dismissed_at = null).
//
// Step 1 — Language + Currency (defaults: $ per Phase 102 spec)
// Step 2 — Log your first transaction (opens AddTransactionModal in
//          tutorial mode with inline tooltips)
// Step 3 — Sync & backup (close + navigate to /transactions)

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n, useLanguage } from '../i18n/index.jsx';
import { db } from './localDb.js';
import { writes, data } from './dataService.js';
import { formatMoney } from './format.js';
import AddTransactionModal from '../components/AddTransactionModal.jsx';

// Symbols offered in the carousel. Matches what CurrencySection lets
// the user add — the only thing we restrict is the initial set so the
// picker stays scannable on mobile. Source of truth is db.settings.currency_symbols
// (parsed JSON array) so users see the same list as Settings → Currency.
const DEFAULT_CURRENCY_SYMBOLS = ['$', '¥', '€', '£'];
const LOCALES = [
  { code: 'zh-Hant', label: '繁體中文' },
  { code: 'zh-Hans', label: '简体中文' },
  { code: 'en',      label: 'English' },
  { code: 'ja',      label: '日本語' },
];

const STEPS = 3;

export default function OnboardingCarousel({ txCount = 0, onComplete }) {
  const { t } = useI18n();
  const { lang, setLang } = useLanguage();
  const navigate = useNavigate();

  // open controls whether the sheet is visible. The trigger in App.jsx
  // sets this true once the boot guards pass.
  const [open, setOpen] = useState(true);
  const [step, setStep] = useState(0);  // 0..STEPS-1
  // Phase 102b: load currency_symbols from db.settings so the picker
  // reflects whatever the user has configured (incl. custom symbols
  // they added in Settings → Currency). Falls back to a sensible
  // default if the row hasn't been written yet (very first boot).
  const [currencySymbols, setCurrencySymbols] = useState(DEFAULT_CURRENCY_SYMBOLS);
  const [draftCurrency, setDraftCurrency] = useState('$');  // default per spec
  const [draftLang, setDraftLang] = useState(lang);
  const [showAddModal, setShowAddModal] = useState(false);
  // Phase 102b: categories are loaded lazily so the embedded
  // AddTransactionModal in Step 2 has data to render. Cached for the
  // life of the carousel; refreshing is unnecessary since new categories
  // added during onboarding would only land after a reload anyway.
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const list = await data.getCategories();
        if (Array.isArray(list)) setCategories(list);
      } catch (e) {
        console.warn('[onboard] could not load categories:', e);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const row = await db.settings.where('key').equals('currency_symbols').first();
        if (!row || row.deleted) return;
        const parsed = JSON.parse(row.value);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setCurrencySymbols(parsed);
          setDraftCurrency(prev => parsed.includes(prev) ? prev : parsed[0]);
        }
      } catch (e) {
        console.warn('[onboard] could not load currency_symbols:', e);
      }
    })();
  }, []);

  // Touch-drag state for swipe-between-steps. Reuses the same gesture
  // pattern as AddTransactionModal (Phase 98) but only horizontal.
  const sheetRef = useRef(null);
  const dragXRef = useRef(null);
  const [dragX, setDragX] = useState(0);

  // ── Step 1: language + currency ───────────────────────────────────
  const handleSaveStep1 = useCallback(async () => {
    try {
      await writes.setSetting('currency', draftCurrency);
    } catch (e) { /* settings write failed — non-fatal, user can retry */ }
    if (draftLang && draftLang !== lang) setLang(draftLang);
    setStep(1);
  }, [draftCurrency, draftLang, lang, setLang]);

  // ── Step 2: log your first transaction ────────────────────────────
  const handleLogNow = useCallback(() => {
    setShowAddModal(true);
  }, []);

  // ── Step 3: sync + backup → done ──────────────────────────────────
  const handleComplete = useCallback(async () => {
    const now = new Date().toISOString();
    try {
      await db.user_prefs.put({
        key: 'onboarding',
        value: {
          completed_at: now,
          dismissed_at: null,
          seen_step: STEPS - 1,
          force_show: null,    // Phase 102b: clear one-shot flag
        },
        updated_at: now,
      });
    } catch (e) { /* swallow — carousel will retrigger next boot */ }
    setOpen(false);
    onComplete?.();
    navigate('/transactions', { replace: true });
  }, [navigate, onComplete]);

  // ── Dismiss entirely (any step) ──────────────────────────────────
  const handleDismiss = useCallback(async () => {
    const now = new Date().toISOString();
    try {
      await db.user_prefs.put({
        key: 'onboarding',
        value: {
          completed_at: null,
          dismissed_at: now,
          seen_step: step,
          force_show: null,    // Phase 102b: clear one-shot flag
        },
        updated_at: now,
      });
    } catch (e) { /* same */ }
    setOpen(false);
    onComplete?.();
    navigate('/transactions', { replace: true });
  }, [step, navigate, onComplete]);

  // ── Skip-to-end convenience (Step 1's "Skip" jumps to Step 3, which
  //    auto-completes; we don't show Step 2 if they haven't picked a
  //    currency yet — there's nothing useful to teach without one).
  const handleStep1Skip = useCallback(() => {
    handleDismiss();
  }, [handleDismiss]);

  // ── Horizontal swipe between steps ───────────────────────────────
  const onTouchStart = useCallback((e) => {
    if (e.touches.length !== 1) return;
    dragXRef.current = e.touches[0].clientX;
  }, []);
  const onTouchMove = useCallback((e) => {
    if (dragXRef.current == null) return;
    const dx = e.touches[0].clientX - dragXRef.current;
    setDragX(dx);
  }, []);
  const onTouchEnd = useCallback((e) => {
    if (dragXRef.current == null) return;
    const dx = (e.changedTouches[0]?.clientX ?? dragXRef.current) - dragXRef.current;
    dragXRef.current = null;
    setDragX(0);
    if (Math.abs(dx) > 80) {
      if (dx < 0 && step < STEPS - 1) setStep(s => s + 1);
      else if (dx > 0 && step > 0) setStep(s => s - 1);
    }
  }, [step]);

  if (!open) return null;

  const dragStyle = dragX ? { transform: `translateX(${dragX}px)` } : {};

  return (
    <>
      <div
        className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center"
        role="dialog"
        aria-modal="true"
        aria-labelledby="onboard-title"
        data-testid="onboard-sheet"
      >
        <div
          ref={sheetRef}
          className="bg-white dark:bg-slate-800 w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl shadow-2xl flex flex-col"
          style={{
            maxHeight: 'min(92vh, 92dvh)',
            ...dragStyle,
            transition: dragX ? 'none' : 'transform 200ms ease-out',
          }}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {/* drag handle */}
          <div className="sm:hidden pt-2 pb-1 flex justify-center" aria-hidden="true">
            <div className="w-12 h-1 rounded-full bg-slate-300 dark:bg-slate-600" />
          </div>

          {/* body */}
          <div className="flex-1 overflow-y-auto p-5 pb-[env(safe-area-inset-bottom)]">
            {step === 0 && (
              <Step1
                t={t}
                draftLang={draftLang}
                setDraftLang={setDraftLang}
                draftCurrency={draftCurrency}
                setDraftCurrency={setDraftCurrency}
                currencySymbols={currencySymbols}
              />
            )}
            {step === 1 && <Step2 t={t} />}
            {step === 2 && <Step3 t={t} />}
          </div>

          {/* footer — step indicator + primary CTA + secondary */}
          <div className="border-t border-slate-200 dark:border-slate-700 p-4 flex items-center gap-3">
            <Dots step={step} total={STEPS} />
            <div className="flex-1" />
            {step > 0 && (
              <button
                onClick={() => setStep(s => s - 1)}
                className="px-4 min-h-[44px] rounded-lg text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
                data-testid="onboard-back"
              >
                {t('onboard.back')}
              </button>
            )}
            {step === 0 && (
              <>
                <button
                  onClick={handleStep1Skip}
                  className="px-4 min-h-[44px] rounded-lg text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
                  data-testid="onboard-skip"
                >
                  {t('onboard.skip')}
                </button>
                <button
                  onClick={handleSaveStep1}
                  className="px-5 min-h-[44px] rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium"
                  data-testid="onboard-next"
                >
                  {t('onboard.next')}
                </button>
              </>
            )}
            {step === 1 && (
              <>
                <button
                  onClick={handleDismiss}
                  className="px-4 min-h-[44px] rounded-lg text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
                  data-testid="onboard-later"
                >
                  {t('onboard.later')}
                </button>
                <button
                  onClick={handleLogNow}
                  className="px-5 min-h-[44px] rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium"
                  data-testid="onboard-log-now"
                >
                  {t('onboard.logNow')}
                </button>
              </>
            )}
            {step === 2 && (
              <button
                onClick={handleComplete}
                className="px-5 min-h-[44px] rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium"
                data-testid="onboard-start"
              >
                {t('onboard.start')}
              </button>
            )}
          </div>
        </div>
      </div>

      {showAddModal && (
        <AddTransactionModal
          open={showAddModal}
          onClose={() => { setShowAddModal(false); setStep(2); }}
          categories={categories}
          onSaved={() => { setShowAddModal(false); setStep(2); }}
          onboard
        />
      )}
    </>
  );
}

function Step1({ t, draftLang, setDraftLang, draftCurrency, setDraftCurrency, currencySymbols }) {
  return (
    <div className="space-y-4">
      <div className="text-4xl" aria-hidden="true">🌐</div>
      <h2 id="onboard-title" className="text-xl font-semibold text-slate-900 dark:text-slate-100">
        {t('onboard.step1.title')}
      </h2>
      <p className="text-sm text-slate-600 dark:text-slate-300">
        {t('onboard.step1.body')}
      </p>

      <div>
        <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
          {t('onboard.step1.langLabel')}
        </label>
        <select
          value={draftLang}
          onChange={e => setDraftLang(e.target.value)}
          data-testid="onboard-lang"
          className="w-full min-h-[44px] px-3 rounded-lg bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100"
        >
          {LOCALES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
        </select>
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
          {t('onboard.step1.curLabel')}
        </label>
        <div className="grid grid-cols-3 gap-2" data-testid="onboard-currency">
          {currencySymbols.map(sym => (
            <button
              key={sym}
              onClick={() => setDraftCurrency(sym)}
              data-testid={`onboard-currency-${sym}`}
              className={[
                'min-h-[44px] rounded-lg text-base font-medium',
                draftCurrency === sym
                  ? 'bg-brand-600 text-white'
                  : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200',
              ].join(' ')}
            >
              {sym}
            </button>
          ))}
        </div>
        <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">
          {t('onboard.step1.preview')}: <span className="font-mono">{formatMoney(1234.5, draftCurrency)}</span>
        </div>
      </div>
    </div>
  );
}

function Step2({ t }) {
  return (
    <div className="space-y-4">
      <div className="text-4xl" aria-hidden="true">📝</div>
      <h2 id="onboard-title" className="text-xl font-semibold text-slate-900 dark:text-slate-100">
        {t('onboard.step2.title')}
      </h2>
      <p className="text-sm text-slate-600 dark:text-slate-300">
        {t('onboard.step2.body')}
      </p>
      <ul className="text-sm text-slate-700 dark:text-slate-200 space-y-2 list-disc pl-5">
        <li>{t('onboard.step2.bullet1')}</li>
        <li>{t('onboard.step2.bullet2')}</li>
        <li>{t('onboard.step2.bullet3')}</li>
      </ul>
      <div className="bg-slate-50 dark:bg-slate-900/40 rounded-lg p-3 text-xs text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700">
        💡 {t('onboard.step2.tipNote')}
      </div>
    </div>
  );
}

function Step3({ t }) {
  return (
    <div className="space-y-4">
      <div className="text-4xl" aria-hidden="true">☁️</div>
      <h2 id="onboard-title" className="text-xl font-semibold text-slate-900 dark:text-slate-100">
        {t('onboard.step3.title')}
      </h2>
      <p className="text-sm text-slate-600 dark:text-slate-300">
        {t('onboard.step3.body')}
      </p>
      <ul className="text-sm text-slate-700 dark:text-slate-200 space-y-2 list-disc pl-5">
        <li>{t('onboard.step3.bullet1')}</li>
        <li>{t('onboard.step3.bullet2')}</li>
      </ul>
      <div className="bg-slate-50 dark:bg-slate-900/40 rounded-lg p-3 text-xs text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700">
        {t('onboard.step3.signinNote')}
      </div>
    </div>
  );
}

function Dots({ step, total }) {
  return (
    <div className="flex gap-1.5" aria-hidden="true" data-testid="onboard-dots">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className={[
            'w-2 h-2 rounded-full transition-colors',
            i === step ? 'bg-brand-600' : 'bg-slate-300 dark:bg-slate-600',
          ].join(' ')}
        />
      ))}
    </div>
  );
}
